import { useCallback, useEffect, useRef } from 'react';

interface UseLongPressOptions {
  /** Hold duration before the long-press fires, in ms. */
  delay?: number;
  /** Pointer movement (px) beyond which the gesture is treated as a
   *  scroll/drag and the long-press is cancelled. */
  moveThreshold?: number;
}

interface LongPressHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerLeave: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  /** Swallows the synthetic click that follows a long-press so the
   *  element's own onClick (e.g. row select) doesn't ALSO fire. */
  onClickCapture: (e: React.MouseEvent) => void;
}

/**
 * Pointer-based long-press detection.
 *
 * Fires `onLongPress` after the pointer is held still for ~`delay` ms.
 * - CANCELS if the pointer moves beyond `moveThreshold` (so it never hijacks
 *   a scroll/drag) or is released/leaves before the timer elapses.
 * - A quick tap (release before the timer) is NOT a long-press — the
 *   element's normal onClick proceeds untouched.
 * - After a long-press fires, the immediately-following synthetic click is
 *   suppressed via `onClickCapture` so the element's onClick doesn't run too.
 *
 * Returns handlers to spread onto the target element.
 */
export function useLongPress(
  onLongPress: () => void,
  { delay = 480, moveThreshold = 10 }: UseLongPressOptions = {},
): LongPressHandlers {
  const timerRef = useRef<number | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  // Set the instant a long-press fires; consumed (and cleared) by the next
  // click so exactly one synthetic click is suppressed.
  const firedRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Clean up any pending timer on unmount.
  useEffect(() => clearTimer, [clearTimer]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Only react to primary touch/pen/mouse presses; ignore secondary
      // buttons so right-click etc. don't arm the timer.
      if (e.button != null && e.button !== 0) return;
      startRef.current = { x: e.clientX, y: e.clientY };
      firedRef.current = false;
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        firedRef.current = true;
        onLongPress();
      }, delay);
    },
    [clearTimer, delay, onLongPress],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (timerRef.current == null || !startRef.current) return;
      const dx = e.clientX - startRef.current.x;
      const dy = e.clientY - startRef.current.y;
      // Moving past the threshold means scroll/drag, not a long-press.
      if (dx * dx + dy * dy > moveThreshold * moveThreshold) {
        clearTimer();
      }
    },
    [clearTimer, moveThreshold],
  );

  const onPointerUp = useCallback(() => {
    // Released before the timer elapsed → a normal tap; cancel the timer and
    // let the element's onClick run as usual.
    clearTimer();
  }, [clearTimer]);

  const onPointerLeave = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const onPointerCancel = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (firedRef.current) {
      // Suppress the click synthesized after the long-press so the row's
      // onClick (select) doesn't fire on top of the menu opening.
      e.preventDefault();
      e.stopPropagation();
      firedRef.current = false;
    }
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave,
    onPointerCancel,
    onClickCapture,
  };
}
